import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import type { ReactionSummary } from '@lens/shared';
import { ReactionBar } from './ReactionBar.tsx';
import { addItemReaction, listItemReactions, removeItemReaction } from './api.ts';

interface ItemReactionBarProps {
  spaceId: string;
  itemId: string;
  /** member+ может реагировать; наблюдателю панель только для чтения. */
  canReact: boolean;
  /** Реакции из item GET — стартовое состояние без мигания пустотой. */
  initial?: ReactionSummary[];
}

/**
 * Контейнер реакций на карточку: держит состояние, грузит актуальные реакции
 * и тогглит add/remove. Самодостаточен — не требует проброса реакций через
 * item-проп грида.
 */
export function ItemReactionBar({ spaceId, itemId, canReact, initial }: ItemReactionBarProps): JSX.Element {
  const [reactions, setReactions] = useState<ReactionSummary[]>(initial ?? []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listItemReactions(spaceId, itemId)
      .then((res) => {
        if (!cancelled) setReactions(res.reactions);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [spaceId, itemId]);

  const handleToggle = useCallback(
    async (emoji: string) => {
      const active = reactions.some((r) => r.emoji === emoji && r.reactedByMe);
      setBusy(true);
      try {
        const res = active
          ? await removeItemReaction(spaceId, itemId, emoji)
          : await addItemReaction(spaceId, itemId, emoji);
        setReactions(res.reactions);
      } catch (err) {
        void message.error(err instanceof Error ? err.message : 'Не удалось изменить реакцию');
      } finally {
        setBusy(false);
      }
    },
    [reactions, spaceId, itemId],
  );

  return <ReactionBar reactions={reactions} canReact={canReact} onToggle={handleToggle} disabled={busy} />;
}
